import { LeftOutlined, SearchOutlined } from '@ant-design/icons'
import { AnalyticsStatistic } from '@components/charts/analytics/Statistic'
import { getTransactionColumns } from '@components/charts/membership/analytics-overview/transactionColumns'
import {
  figureValue,
  formatPercent,
  MIXED_CURRENCIES_CAPTION,
} from '@components/charts/membership/revenue/figures'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import useResolveProgramToPlans from '@hooks/useResolveProgramToPlans'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import {
  FetchMembershipAnalyticsStatisticsParams,
  ResponseMembershipTransactionsAnalyticsStatistics,
} from '@models/membership/MembershipAnalyticsStatistics'
import { MembershipTransaction } from '@models/membership/MembershipTransaction'
import { fetchAnalyticsStatistics } from '@services/AnalyticsStatistics'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getMembershipTransactions,
  selectMembershipTransactions,
  setMembershipTransactionQuery,
} from '@store/membership/MembershipTransactionReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { useSBRM } from '@supplement-bacon/sbrm-react'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { Entity } from '@utils/Entities'
import { analyticsRangePresets } from '@utils/membershipRangePresets'
import { Permission } from '@utils/permissions'
import {
  dateRangeFromFilters,
  dateRangeToFilters,
} from '@utils/tableDateRangeFilter'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Input,
  Row,
  Select,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const { RangePicker } = DatePicker
const { Text } = Typography

/**
 * Far enough back to cover every payment ever recorded: the tiles use it when
 * the table has no date filter, so both describe the same set. The statistics
 * endpoint requires a window, hence an open one rather than none.
 */
const OPEN_WINDOW_START = '2000-01-01'

// Values match the backend SubscriptionPayment::STATUS_* constants (capitalized),
// which the status filter query matches on.
const STATUS_OPTIONS = [
  { value: 'Completed', label: 'Completed' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Pending', label: 'Pending' },
]

/**
 * Translates the table's own filters into what
 * `analytics/statistics?entity=membership` accepts, so the tiles aggregate
 * exactly the rows the table lists - the program id, plan ids, status and
 * member search all go through untouched.
 */
const buildKpiParams = (
  query: TableParams
): FetchMembershipAnalyticsStatisticsParams => {
  // `TableParams['filters']` unions in the activity-log shape, which has none of
  // these keys; this table only ever passes the AntD record.
  const filters = query.filters as
    | Record<string, (string | number)[] | null>
    | undefined

  // Plans drive the scope; `programs` is only present on links that predate the
  // single Program / plan tree (Revenue page, old bookmarks) and keeps working.
  const programIds = (filters?.programs ?? []).map(Number)
  const planIds = (filters?.plans ?? []).map(Number)
  const statuses = (filters?.status ?? []).map(String)
  const member = query.search?.member ? String(query.search.member) : undefined

  const start = filters?.startDate?.[0]
  const end = filters?.endDate?.[0]

  return {
    entity: 'membership',
    kind: 'membership-transactions',
    startDate: start ? String(start) : OPEN_WINDOW_START,
    endDate: end ? String(end) : dayjs().format('YYYY-MM-DD'),
    programIds: programIds.length ? programIds : undefined,
    planIds: planIds.length ? planIds : undefined,
    filters: statuses.length ? { status: statuses } : undefined,
    search: member ? { member } : undefined,
  }
}

const TransactionsPage = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { isOpen } = useSBRM()
  const transactions = useAppSelector(selectMembershipTransactions())
  const { query, isLoading } = useAppSelector(
    (state) => state.membershipTransaction
  )

  const [kpis, setKpis] =
    useState<ResponseMembershipTransactionsAnalyticsStatistics>()
  const [isKpisLoading, setIsKpisLoading] = useState<boolean>(false)
  const [kpisError, setKpisError] = useState<string>()

  // Only the newest request may write the tiles: filters change faster than the
  // aggregates come back, and a slower earlier response landing last used to
  // repaint the previous filter's figures over the current ones.
  const latestKpiRequest = useRef(0)

  const fetchKpis = async (newQuery: TableParams) => {
    const requestId = ++latestKpiRequest.current
    setIsKpisLoading(true)
    setKpisError(undefined)

    try {
      const { data } =
        await fetchAnalyticsStatistics<ResponseMembershipTransactionsAnalyticsStatistics>(
          buildKpiParams(newQuery)
        )

      if (requestId !== latestKpiRequest.current) return
      setKpis(data)
    } catch (error) {
      if (requestId !== latestKpiRequest.current) return

      console.error('Error fetching transaction KPIs:', error)
      setKpisError('Failed to fetch data.')
      // Dropped rather than kept: an error message next to the previous
      // filter's numbers reads as if those numbers were still current.
      setKpis(undefined)
    } finally {
      if (requestId === latestKpiRequest.current) setIsKpisLoading(false)
    }
  }

  const fetchTransactions = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      columnKey: params.sort.columnKey ?? 'date',
      order: params.sort.order ?? 'descend',
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
      filters: params.filters,
      search: params.search,
    }

    dispatch(setMembershipTransactionQuery(newQuery))
    dispatch(getMembershipTransactions(newQuery))
    fetchKpis(newQuery)
  }

  const { handleFiltersChange, handleTableChange, params, setSearchParams } =
    useTableUrlSync<MembershipTransaction>({
      onParamsChange: fetchTransactions,
    })

  useResolveProgramToPlans(params.filters, setSearchParams)

  const hasActiveFilters =
    ['status', 'plans', 'programs', 'startDate', 'endDate'].some(
      (key) => (params.filters[key] ?? []).length > 0
    ) || Boolean(params.search.member)

  // The SBRM "View" drawer never mutates a transaction (view-only, comment
  // #13) but the list is refetched on close anyway so it never shows stale
  // rows if the underlying data changed elsewhere in the meantime.
  //
  // Goes through the page fetcher with the URL params, never through the store
  // query: on mount that query is still `initialQuery` (no filter, page 1) and
  // its request, dispatched after the URL-driven one, used to land last and
  // overwrite it - a deep link with filters showed unfiltered rows.
  // Only on a close (open -> closed): the URL-driven load already covers the
  // mount, so refetching then would fire the same request twice.
  const wasOpen = useRef(false)
  useEffect(() => {
    const justClosed = wasOpen.current && !isOpen
    wasOpen.current = isOpen
    if (!justClosed) return

    fetchTransactions(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Sorting only here: this page wires `onChange`, the overview preview does not.
  const columns = getTransactionColumns({
    withActions: true,
    withSorting: true,
  })

  return (
    <Page permissions={Permission.viewRevenueMembership} noWrapWithCol>
      <Col span={24}>
        <Flex gap={16} align="center">
          <Button
            aria-label="Back to Revenue"
            icon={<LeftOutlined />}
            onClick={() => navigate('/membership/revenue')}
          />
          <PageTitle title="Transactions" level={4} style={{ margin: 0 }} />
        </Flex>
      </Col>
      <Col span={24}>
        <TableFilters
          wrapRows
          initialValues={{
            status: params.filters.status,
            plans: params.filters.plans?.map(Number),
            member: params.search.member,
            dateRange: dateRangeFromFilters(params.filters),
          }}
          onValuesChange={(filters: any, search: any) => {
            const { dateRange, ...otherFilters } = filters

            handleFiltersChange(
              { ...otherFilters, ...dateRangeToFilters(dateRange) },
              search
            )
          }}
          // Figma 798-3975: no labels, placeholders inside the controls, and the
          // member search moved next to the date range on the right.
          left={[
            {
              name: 'status',
              type: 'filter',
              style: { width: 160 },
              input: (
                <Select
                  options={STATUS_OPTIONS}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="Status"
                  allowClear
                />
              ),
            },
            {
              name: 'plans',
              type: 'filter',
              style: { width: 200 },
              input: (
                // One tree for programs and plans: checking a program checks all
                // of its plans. `f_programs` in an incoming URL (links from the
                // Revenue page, old bookmarks) is still honoured by the fetch
                // below, it just is not drawn in this control.
                <SBAsyncSelect
                  multiple
                  overrideDisplay="tree"
                  treeSelectProps={{ maxTagCount: 'responsive' }}
                  placeholder="Program / plan"
                  entity={Entity.membershipPlan}
                  allowClear
                />
              ),
            },
          ]}
          right={[
            {
              name: 'member',
              type: 'search',
              style: { width: 300, maxWidth: 300 },
              input: (
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Search member..."
                  allowClear
                />
              ),
            },
            {
              name: 'dateRange',
              type: 'filter',
              style: { width: 240, maxWidth: 240 },
              input: (
                <RangePicker
                  className="w-full"
                  placement="bottomRight"
                  presets={analyticsRangePresets()}
                />
              ),
            },
          ]}
        />
      </Col>
      {/* KPIs sit under the filters, which drive them (Figma 798-4007). */}
      <Col span={24}>
        <Row gutter={[24, 24]}>
          <Col flex="1">
            <AnalyticsStatistic
              variant="tile"
              title="Total collected"
              value={
                kpis &&
                (kpis.totalCollected?.value == null
                  ? '—'
                  : analyticsCurrency(
                      kpis.totalCollected.value,
                      true,
                      kpis.currency
                    ))
              }
              caption={
                kpis && kpis.totalCollected?.value == null
                  ? MIXED_CURRENCIES_CAPTION
                  : undefined
              }
              tone="success"
              isLoading={isKpisLoading}
            />
          </Col>
          <Col flex="1">
            <AnalyticsStatistic
              variant="tile"
              title="Transactions"
              value={kpis?.transactionsCount?.value}
              tone="info"
              isLoading={isKpisLoading}
            />
          </Col>
          <Col flex="1">
            <AnalyticsStatistic
              variant="tile"
              title="Failed"
              value={kpis?.failedCount?.value}
              tone="error"
              isLoading={isKpisLoading}
              trend={
                figureValue(kpis?.failedRate) !== null
                  ? {
                      label: `${formatPercent(figureValue(kpis?.failedRate))} of transactions`,
                    }
                  : undefined
              }
            />
          </Col>
        </Row>
        {kpisError && <Text type="danger">{kpisError}</Text>}
      </Col>
      <Col span={24}>
        {/* Rows and pager live inside one bordered card (Figma 798-4029). */}
        <Card
          style={{ borderRadius: 8 }}
          styles={{ body: { padding: 0, overflow: 'hidden' } }}
        >
          <SBTable
            columns={columns}
            dataSource={transactions}
            loading={isLoading}
            onChange={handleTableChange}
            pagination={{
              ...query.pagination,
              // No `showSizeChanger`: the design has no page size selector.
              showSizeChanger: false,
              // `marginRight: auto` pins the count to the left edge and the
              // pager to the right, as the design lays the footer out.
              showTotal: (total, range) => (
                <span style={{ marginRight: 'auto' }}>
                  {`Showing ${range[0]}-${range[1]} of ${total}`}
                </span>
              ),
              style: {
                padding: 20,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
              },
            }}
            scroll={{ x: 'max-content' }}
            rowKey="id"
            empty={{
              description: hasActiveFilters
                ? 'No transactions match these filters'
                : 'No transactions found',
            }}
          />
        </Card>
      </Col>
    </Page>
  )
}

export default TransactionsPage
